import React from "react";
import PropTypes from "prop-types";

// TableToolbar — the legacy strip that sits above a table: the select-all-rows checkbox
// and the "Download (Excel)" button, plus a slot for page-specific actions.
//
// Class strings are copied verbatim from src/components/orderTable/table-layout.jsx
// (the select-all block at :195-207 and the Download button at :224), minus the classes
// that emit no CSS in this build (`text-md`). Do not hand-convert any of them to px:
// 1rem is 13px app-wide, so px-4/py-3/rounded-2xl already mean the right thing, and
// `bg-white`/`text-white`/`text-md`/`font-mono` produce nothing here — the download
// button's white text comes from `text-white-50`, not `text-white`.
//
// The wrapper mirrors legacy's toolbar row (`flex flex-row gap-5 ... relative`). `relative`
// matters: legacy's right-hand action (Print Multiple Address) positions itself with
// `absolute right-0`, so a page passing that button through `children` needs a positioned
// ancestor here.
//
// Purely presentational — no selection state, no data. `onDownloadExcel` is the caller's
// own callback; the page is what calls
// DownLoadTable(fileName, sheetName, headers, rows) from src/utils/DownloadtablesInExcel,
// because only the page knows its column headers and current rows. Omit the prop and the
// button is not rendered at all (legacy gates it on the DownloadExcel functionality flag).
function TableToolbar({ allSelected, onToggleAll, onDownloadExcel, children }) {
    return (
        <div className="flex flex-row gap-5 items-center relative">
            <div className="mt-4 text-black px-4 py-3 rounded-2xl font-sans font-semibold flex flex-row gap-2 items-center">
                <input
                    type="checkbox"
                    // Legacy's checkbox is a single toggle: it selects every row, or clears
                    // the selection if everything is already selected. The parent owns that
                    // decision — this only reports the toggle.
                    checked={allSelected}
                    onChange={onToggleAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <p>{allSelected ? "Unselect" : "Select"} All rows</p>
            </div>
            {onDownloadExcel && (
                <button
                    type="button"
                    className="bg-elm-500 text-white-50 px-4 py-3 rounded-2xl font-sans font-semibold flex justify-center"
                    onClick={onDownloadExcel}
                >
                    Download (Excel)
                </button>
            )}
            {children}
        </div>
    );
}

TableToolbar.propTypes = {
    // True when every row on the page is selected — drives both the checkbox and the
    // Select/Unselect wording.
    allSelected: PropTypes.bool,
    onToggleAll: PropTypes.func,
    // Omit to hide the Download (Excel) button. The caller invokes DownLoadTable itself.
    onDownloadExcel: PropTypes.func,
    // Extra page-specific actions rendered at the end of the strip, e.g. Orders'
    // "Print Address Labels".
    children: PropTypes.node,
};

TableToolbar.defaultProps = {
    allSelected: false,
    onToggleAll: () => {},
    onDownloadExcel: null,
    children: null,
};

export default TableToolbar;
